import { Link, useRouterState } from '@tanstack/react-router';
import { JitLogo } from '@/brand/JitLogo';
import { useAuth } from '@/lib/auth/AuthProvider';
import { cn } from '@/lib/cn';
import { NAV_ITEMS, type NavItem } from './nav-items';

/*
  Primary navigation.

  Structure, and why:

  * <nav aria-label="Primary"> wrapping a <ul>. A list of links is a list, and a screen
    reader user gets "7 items" and can walk them with list navigation.
  * aria-current="page" on the active link, which is how assistive technology is told where
    you are. A colour change alone does not convey it.
  * The active item also carries a solid left rail and a heavier weight, so the current page
    is identifiable without colour.
  * Items the technician has no permission for are not rendered. That is tidiness, not
    security; every route and every API call is enforced server side regardless.
*/

function isActive(currentPath: string, item: NavItem): boolean {
  if (item.to === '/') {
    return currentPath === '/';
  }
  return currentPath === item.to || currentPath.startsWith(`${item.to}/`);
}

export interface SidebarProps {
  /** Called after a navigation, so the mobile drawer can close itself. */
  readonly onNavigate?: () => void;
  readonly className?: string;
}

export function Sidebar({ onNavigate, className }: SidebarProps): React.JSX.Element {
  const { canAny, user } = useAuth();
  const currentPath = useRouterState({ select: (state) => state.location.pathname });

  const visible = NAV_ITEMS.filter(
    (item) => item.permissions.length === 0 || canAny(item.permissions),
  );

  return (
    <div className={cn('flex h-full flex-col border-r border-border bg-surface', className)}>
      <div className="flex h-14 shrink-0 items-center px-4">
        <Link
          to="/"
          onClick={onNavigate}
          className="flex items-center rounded-[var(--radius-control)] px-1 py-1"
          aria-label="JIT console home"
        >
          <JitLogo height={26} />
        </Link>
      </div>

      <nav aria-label="Primary" className="flex-1 overflow-y-auto px-2 pb-4">
        <ul className="flex flex-col gap-0.5">
          {visible.map((item) => {
            const active = isActive(currentPath, item);
            const Icon = item.Icon;
            return (
              <li key={item.to}>
                <Link
                  to={item.to}
                  onClick={onNavigate}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'group relative flex items-center gap-2.5 rounded-[var(--radius-control)] py-2 pr-2 pl-3 text-sm transition-colors',
                    active
                      ? 'bg-accent-soft font-semibold text-accent-soft-fg'
                      : 'font-medium text-fg-muted hover:bg-surface-hover hover:text-fg',
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      'absolute top-1.5 bottom-1.5 left-0 w-0.5 rounded-full',
                      active ? 'bg-accent' : 'bg-transparent',
                    )}
                  />
                  <Icon className="size-4 shrink-0" aria-hidden="true" />
                  <span className="truncate">{item.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="shrink-0 border-t border-border px-4 py-3">
        <p className="text-[11px] leading-relaxed text-fg-subtle">
          <span className="font-semibold text-fg-muted">{user?.tenantName ?? 'JIT'}</span>
          <br />
          JUICE Interactive Tool, console 0.1.0
        </p>
      </div>
    </div>
  );
}
